import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {AlertService, CampaignService, LoaderService, UserService, FunnelService} from '../_services';
import {Form} from '../_forms';
import {Campaign, Funnel, FunnelType, User} from '../_models';
import {takeUntil} from 'rxjs/operators';

@Component({
  moduleId: module.id.toString(),
  selector: 'funnel-tree-actions',
  templateUrl: './funnel-tree-actions.component.html'
})
export class FunnelTreeActionsComponent extends Form implements OnInit{
  user: User;
  campaignFunnels: (string | number)[] = []
  campaignFunnelFieldName: string = null;
  campaign: Campaign = null;

  @Input('isTemplate') isTemplate = false;
  @Input('isPublishing') isPublishing = false;
  @Input('funnel') funnel: Funnel;
  @Input('baseRoute') baseRoute = 'path';
  @Output('publish') onPublish: EventEmitter<any> = new EventEmitter<any>();
  @Output('preview') onPreview: EventEmitter<any> = new EventEmitter<any>();
  @Output('copy') onCopy: EventEmitter<any> = new EventEmitter<any>();
  @Output('copyToClipboard') onCopyToClipboard: EventEmitter<any> = new EventEmitter<any>();
  @Output('clone') onClone: EventEmitter<any> = new EventEmitter<any>();
  @Output('saveTemplate') onSaveAsTemplate: EventEmitter<any> = new EventEmitter<any>();
  @Output('delete') onDelete: EventEmitter<any> = new EventEmitter<any>();
  @Output('deleteUnassigned') onDeleteUnassigned: EventEmitter<any> = new EventEmitter<any>();

  constructor(protected router: Router,
              protected location: Location,
              protected alertService: AlertService,
              private userService: UserService,
              protected campaignService: CampaignService,
              protected loaderService: LoaderService,
              private funnelService: FunnelService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.userService.getCurrent()
      .subscribe(
        (user: User) => {
          this.user = user;
        },
        error => {
          this.handleError(error);
        }
      );

    this.processAssignedFunnels(this.campaignService.getCurrentCampaign());
    this.campaignService.currentCampaignUpdate$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((campaign: Campaign) => {
        this.processAssignedFunnels(campaign);
      });
  }

  processAssignedFunnels(campaign: Campaign) {
    if (campaign) {
      this.campaign = campaign;
      switch (this.funnel.resourcetype) {
        case FunnelType.Voice:
          this.campaignFunnels = this.campaign.voice_entry_funnels;
          this.campaignFunnelFieldName = 'voice_entry_funnels';
          break;
        case FunnelType.SMS:
          this.campaignFunnels = this.campaign.sms_entry_funnels;
          this.campaignFunnelFieldName = 'sms_entry_funnels';
          break;
        default:
          this.campaignFunnels = this.campaign.visual_entry_funnels;
          this.campaignFunnelFieldName = 'visual_entry_funnels';
      }
    }
  }

  get isProductFunnel() : boolean {
    return (this.funnel.resourcetype === FunnelType.Product);
  }

  get editable() : boolean {
    return !this.isTemplate || this.user.is_staff;
  }

  get isPublicDefault() {
    return this.funnel && this.funnel.is_public_default;
  }

  get canSetAsPublicDefault() {
    return this.funnel && this.funnel.use_intents && this.user && this.user.is_staff && this.funnel.is_public;
  }

  togglePublicDefault(event) {
    if (this.canSetAsPublicDefault) {
      const isPublicDefault: boolean = event.target.checked;
      this.loaderService.show();
      this.funnelService.patch(this.funnel.id, {
        resourcetype: this.funnel.resourcetype,
        is_public_default: isPublicDefault
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (funnel: Funnel) => {
            this.funnel = funnel;
            this.loaderService.hide();
            let message = isPublicDefault ? 'Set as public default' : 'Cleared public default';
            this.alertService.success(message);
          },
          error => {
            this.handleError(error);
            this.loaderService.hide();
          }
        );
    }
  }

  get isCampaignAssigned() {
    return this.campaign && this.funnel && this.campaignFunnels.includes(this.funnel.id as string | number);
  }

  get canShowToggleCampaignAssignment() {
    return this.campaign && this.funnel && (
      this.funnel.resourcetype === FunnelType.Visual ||
      this.funnel.resourcetype === FunnelType.CustomerPortal ||
      this.funnel.resourcetype === FunnelType.Hybrid ||
      this.funnel.resourcetype === FunnelType.Voice ||
      this.funnel.resourcetype === FunnelType.SMS);
  }

  toggleCampaignAssignment(event) {
    const isAssigned: boolean = event.target.checked;
    let updatedFunnels = [...this.campaignFunnels];

    if (isAssigned) {
      if (!updatedFunnels.includes(this.funnel.id)) {
        updatedFunnels.push(this.funnel.id);
      } else {
        return
      }
    } else {
      updatedFunnels = updatedFunnels.filter(id => id !== this.funnel.id);
    }

    let update = {};
    update[this.campaignFunnelFieldName] = updatedFunnels;
    this.loaderService.show();
    this.campaignService.patch(this.campaign.id, update)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (updatedCampaign) => {
          this.campaign = updatedCampaign;
          this.campaignFunnels = updatedCampaign[this.campaignFunnelFieldName] || [];
          this.loaderService.hide();
          let message = isAssigned ? 'Added path to ' : 'Removed path from ';
          message += this.campaign.display_name;
          this.alertService.success(message);
        },
        error => {
          this.handleError(error);
          this.loaderService.hide();
        }
      );
  }
}
